import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export const useMatchNavigation = ({
    displayedStatements,
    showOnlyHighlighted,
    showOnlyHighlightedButtonVisible,
}) => {
    const matchedIndices = useMemo(() => {
        const result = []
        for (let i = 0; i < displayedStatements.length; i++) {
            if (!displayedStatements[i].grayedOut) result.push(i)
        }
        return result
    }, [displayedStatements])
    const matchCount = matchedIndices.length

    // Cursor reset keys off this (not matchCount) so a filter swap that
    // preserves the count but changes which rows match still resets.
    const matchedUidSignature = useMemo(
        () =>
            matchedIndices
                .map(i => displayedStatements[i].uid)
                .join(','),
        [matchedIndices, displayedStatements]
    )

    const [activeMatchIdx, setActiveMatchIdx] = useState(0)
    const virtuosoRef = useRef(null)
    const prevMatchedSignatureRef = useRef(matchedUidSignature)
    const prevNavigatorVisibleRef = useRef(false)

    const navigatorVisible =
        showOnlyHighlightedButtonVisible && !showOnlyHighlighted
    const hasMatches = matchCount > 0

    const activeMatchUid =
        navigatorVisible && hasMatches
            ? displayedStatements[matchedIndices[activeMatchIdx]]?.uid ?? null
            : null

    const scrollMatchIntoView = useCallback(
        nextIdx => {
            if (!virtuosoRef.current) return
            const dataIndex = matchedIndices[nextIdx]
            if (dataIndex == null) return
            virtuosoRef.current.scrollToIndex({
                index: dataIndex,
                align: 'center',
                behavior: 'auto',
            })
        },
        [matchedIndices]
    )

    useEffect(() => {
        const matchedSetChanged =
            prevMatchedSignatureRef.current !== matchedUidSignature
        const navigatorJustAppeared =
            !prevNavigatorVisibleRef.current && navigatorVisible

        if (matchedSetChanged) {
            prevMatchedSignatureRef.current = matchedUidSignature
            setActiveMatchIdx(0)
        }
        prevNavigatorVisibleRef.current = navigatorVisible

        if (!hasMatches) return

        // rAF: let Virtuoso settle measurements on the swapped data first.

        // Mode B (show all rows, gray the non-matches): center the first match.
        if (navigatorVisible && (matchedSetChanged || navigatorJustAppeared)) {
            const raf = requestAnimationFrame(() => scrollMatchIntoView(0))
            return () => cancelAnimationFrame(raf)
        }

        // Mode A (show only matches): reset scroll to top so matches are in viewport.
        if (
            showOnlyHighlighted &&
            showOnlyHighlightedButtonVisible &&
            matchedSetChanged
        ) {
            const raf = requestAnimationFrame(() =>
                virtuosoRef.current?.scrollToIndex({
                    index: 0,
                    align: 'start',
                    behavior: 'auto',
                })
            )
            return () => cancelAnimationFrame(raf)
        }
    }, [
        matchedUidSignature,
        navigatorVisible,
        hasMatches,
        scrollMatchIntoView,
        showOnlyHighlighted,
        showOnlyHighlightedButtonVisible,
    ])

    const goToPrev = useCallback(() => {
        if (matchCount === 0) return
        setActiveMatchIdx(i => {
            const next = i <= 0 ? matchCount - 1 : i - 1
            scrollMatchIntoView(next)
            return next
        })
    }, [matchCount, scrollMatchIntoView])

    const goToNext = useCallback(() => {
        if (matchCount === 0) return
        setActiveMatchIdx(i => {
            const next = i >= matchCount - 1 ? 0 : i + 1
            scrollMatchIntoView(next)
            return next
        })
    }, [matchCount, scrollMatchIntoView])

    return {
        virtuosoRef,
        activeMatchUid,
        navigatorVisible,
        hasMatches,
        activeMatchIdx,
        matchCount,
        goToPrev,
        goToNext,
    }
}
